import { parseDecimal } from '@catering/ui';
import { z } from 'zod';

import { ApiError } from '../../api/client';
import type { MenuFormInput } from '../../menus/api';

/**
 * Mirrors `apps/api` `createMenuSchema` (`menu.dto.ts`), with one deviation:
 * `basePrice` is entered in pesos (what a person types) and transformed to
 * `basePriceCents` on submit, since the API's integer-cents contract is not
 * something a form field should ask a user to compute by hand.
 */
export const menuFormSchema = z.object({
  code: z.string().trim().min(1, 'El código es obligatorio').max(50),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  description: z.string().trim().max(2000),
  serviceTypeId: z
    .string()
    .min(1, 'Seleccioná un tipo de servicio')
    .uuid('Seleccioná un tipo de servicio'),
  basePrice: z
    .string()
    .trim()
    .min(1, 'El precio base es obligatorio')
    .refine((value) => (parseDecimal(value) ?? 0) > 0, {
      message: 'Ingresá un precio mayor a cero, por ejemplo 1500,50',
    })
    .transform((value) => Math.round((parseDecimal(value) ?? 0) * 100)),
  imageUrl: z.string().trim().max(2000),
  preparationNotes: z.string().trim().max(2000),
});

/** Validated/transformed shape passed to the submit handler (post-resolver): `basePrice` is already integer cents. */
export type MenuFormValues = z.infer<typeof menuFormSchema>;

/** Raw, unvalidated shape bound to the inputs: `basePrice` is still the pesos string a person types. */
export interface MenuFormFields {
  code: string;
  name: string;
  description: string;
  serviceTypeId: string;
  basePrice: string;
  imageUrl: string;
  preparationNotes: string;
}

export const DEFAULT_FORM_VALUES: MenuFormFields = {
  code: '',
  name: '',
  description: '',
  serviceTypeId: '',
  basePrice: '',
  imageUrl: '',
  preparationNotes: '',
};

export type FieldErrorTarget = 'code' | 'serviceTypeId';

export function toFormInput(values: MenuFormValues): MenuFormInput {
  return {
    code: values.code,
    name: values.name,
    serviceTypeId: values.serviceTypeId,
    basePriceCents: values.basePrice,
    ...(values.description === '' ? {} : { description: values.description }),
    ...(values.imageUrl === '' ? {} : { imageUrl: values.imageUrl }),
    ...(values.preparationNotes === '' ? {} : { preparationNotes: values.preparationNotes }),
  };
}

/**
 * Server errors never have their `message` parsed: the `code` field on
 * 409/422 bodies (`CatalogExceptionFilter`) drives the mapping, same
 * convention as `customer-form-schema.ts`'s `mapCreateError`.
 */
export function mapMenuFormError(error: unknown): {
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
} {
  if (error === undefined || error === null) {
    return {};
  }

  if (!(error instanceof ApiError)) {
    return { formError: 'Ocurrió un error de conexión. Intentá de nuevo.' };
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'MENU_CODE_DUPLICATE') {
    return { fieldError: { field: 'code', message: 'Ya existe un menú activo con ese código' } };
  }

  if (error.status === 422 && code === 'SERVICE_TYPE_INACTIVE') {
    return {
      fieldError: { field: 'serviceTypeId', message: 'Ese tipo de servicio está inactivo' },
    };
  }

  return { formError: genericMessage(error) };
}

/**
 * Used by the detail page's tabs (dietary tags, allergens, preparation
 * notes) and by the archive action: these actions have no dedicated form
 * field, so a single banner message is enough (`menus/spec.md` → "Error de
 * coherencia mostrado en el formulario").
 */
export function mapMenuPatchError(error: unknown): string {
  if (error === undefined || error === null) {
    return 'Ocurrió un error inesperado. Intentá de nuevo.';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'DIETARY_TAG_ALLERGEN_CONFLICT') {
    return 'Este cambio genera un conflicto entre una etiqueta dietaria y un alérgeno declarado (por ejemplo, «sin TACC» y «gluten»). No se aplicó.';
  }

  if (error.status === 422 && code === 'DIETARY_TAG_INACTIVE') {
    return 'Esa etiqueta dietaria está inactiva y no se puede asignar.';
  }

  if (error.status === 422 && code === 'ALLERGEN_INACTIVE') {
    return 'Ese alérgeno está inactivo y no se puede declarar.';
  }

  if (error.status === 422 && code === 'SERVICE_TYPE_INACTIVE') {
    return 'Ese tipo de servicio está inactivo.';
  }

  if (error.status === 409 && code === 'MENU_ARCHIVED') {
    return 'El menú está archivado y no se puede editar.';
  }

  if (error.status === 409 && code === 'MENU_ALREADY_ARCHIVED') {
    return 'El menú ya está archivado.';
  }

  if (error.status === 409 && code === 'MENU_CODE_DUPLICATE') {
    return 'Ya existe un menú activo con ese código.';
  }

  return genericMessage(error);
}

function genericMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}
